/**
 * Flaneur OSM Recorder — Export Module
 * SPDX-License-Identifier: GPL-3.0-or-later
 *
 * Exports session nodes to OSM XML, GPX, or GeoJSON.
 * OSM XML is the primary format for JOSM import.
 *
 * JOSM import workflow:
 *   File › Open the .osm file → nodes appear as new layer
 *   Review, adjust, add detail → upload through JOSM normally
 *
 * Note on photos: photos are NOT embedded in exports.
 * They must be uploaded externally (Mapillary / Wikimedia Commons)
 * and the URL linked as a tag (e.g. image=https://...) in JOSM.
 */

// ─── Combining sessions ───────────────────────────────────────────────────

/**
 * Fold several sessions into one synthetic session for a single export file.
 *
 * **Node ids must be reissued.** Ids are negative and derived per session, so
 * every session starts again at −1: concatenating two sessions produces
 * duplicate ids, and JOSM rejects a layer that contains them — the same class
 * of failure as the module-counter bug recorded in CLAUDE.md, arriving by a
 * different route. Renumbering across the whole set is what makes a combined
 * file loadable at all.
 *
 * Nothing is written back to storage: the stored sessions keep their own ids,
 * and this object exists only long enough to be serialized.
 *
 * @param {object[]} sessions in the order they should appear in the file
 * @returns {{id: string, name: string, createdAt: string, updatedAt: string,
 *            nodes: object[], sourceSessions: {id: string, name: string,
 *            nodeCount: number}[]}}
 */
export function mergeSessions(sessions) {
  const withNodes = sessions.filter((session) => session?.nodes?.length);
  const nodes = [];
  let nextId = -1;

  for (const session of withNodes) {
    for (const node of session.nodes) {
      nodes.push({ ...node, id: nextId--, sessionId: session.id });
    }
  }

  const created = withNodes.map((s) => s.createdAt).filter(Boolean);

  return {
    id: `combined-${withNodes.length}`,
    name: `Flaneur combined (${withNodes.length} sessions)`,
    // The span the file covers starts at the earliest session it contains, so
    // the filename dates the survey rather than the moment of export.
    createdAt: created.length ? created.slice().sort()[0] : new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    nodes,
    sourceSessions: withNodes.map((s) => ({
      id: s.id,
      name: s.name,
      nodeCount: s.nodes.length,
    })),
  };
}

// ─── OSM XML ──────────────────────────────────────────────────────────────

export function toOsmXml(session) {
  const lines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<!-- Generated by Flaneur OSM Recorder — session: ${session.id} -->`,
    `<!-- Created: ${session.createdAt} | Nodes: ${session.nodes.length} -->`,
    '<!-- SPDX-License-Identifier: ODbL-1.0 (data) — upload via JOSM after review -->',
  ];

  // Which sessions went into a combined file, recorded in comments rather than
  // tags: provenance is for the person reviewing the layer, and inventing an
  // OSM key for it would push Flaneur's internal bookkeeping into OSM itself.
  for (const source of session.sourceSessions ?? []) {
    lines.push(`<!-- Source session: ${escXml(source.id)} — ${source.nodeCount} nodes -->`);
  }

  lines.push('<osm version="0.6" generator="flaneur-osm-recorder">');

  for (const node of session.nodes) {
    const attrs = [
      `id="${node.id}"`,
      `lat="${node.lat.toFixed(7)}"`,
      `lon="${node.lon.toFixed(7)}"`,
      'action="create"',
      `timestamp="${node.timestamp}"`,
    ].join(' ');

    lines.push(`  <node ${attrs}>`);

    for (const [k, v] of Object.entries(node.tags)) {
      lines.push(`    <tag k="${escXml(k)}" v="${escXml(v)}"/>`);
    }

    if (node.note) {
      lines.push(`    <tag k="note" v="${escXml(node.note)}"/>`);
    }

    if (node.accuracy_m != null) {
      lines.push(`    <tag k="flaneur:accuracy" v="${node.accuracy_m.toFixed(1)}m"/>`);
    }

    lines.push('  </node>');
  }

  lines.push('</osm>');
  return lines.join('\n');
}

// ─── GPX ──────────────────────────────────────────────────────────────────

export function toGpx(session) {
  const lines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="flaneur-osm-recorder"',
    '  xmlns="http://www.topografix.com/GPX/1/1"',
    '  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"',
    '  xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">',
    `  <metadata><name>${escXml(session.name)}</name><time>${session.createdAt}</time></metadata>`,
  ];

  for (const node of session.nodes) {
    const tagStr = Object.entries(node.tags)
      .map(([k, v]) => `${k}=${v}`)
      .join('; ');

    lines.push(`  <wpt lat="${node.lat.toFixed(7)}" lon="${node.lon.toFixed(7)}">`);
    lines.push(`    <time>${node.timestamp}</time>`);
    lines.push(`    <name>${escXml(tagStr.split(';')[0])}</name>`);

    const desc = [tagStr, node.note ? `note: ${node.note}` : ''].filter(Boolean).join(' | ');
    lines.push(`    <cmt>${escXml(desc)}</cmt>`);
    lines.push(`    <desc>${escXml(desc)}</desc>`);
    lines.push('  </wpt>');
  }

  lines.push('</gpx>');
  return lines.join('\n');
}

// ─── GeoJSON ──────────────────────────────────────────────────────────────

export function toGeoJson(session) {
  const features = session.nodes.map((node) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [parseFloat(node.lon.toFixed(7)), parseFloat(node.lat.toFixed(7))],
    },
    properties: {
      ...node.tags,
      note: node.note || '',
      timestamp: node.timestamp,
      accuracy_m: node.accuracy_m,
      flaneur_id: node.id,
      // Only present in a combined export, where a reader may want to know
      // which survey a point came from. Omitted entirely otherwise.
      ...(node.sessionId ? { flaneur_session: node.sessionId } : {}),
    },
  }));

  return JSON.stringify(
    {
      type: 'FeatureCollection',
      name: session.name,
      generated_by: 'flaneur-osm-recorder',
      session_id: session.id,
      ...(session.sourceSessions ? { source_sessions: session.sourceSessions } : {}),
      features,
    },
    null,
    2
  );
}

// ─── Download helper ──────────────────────────────────────────────────────

export function downloadText(content, filename, mimeType) {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.className = 'visually-hidden-input';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 1000);
}

export function exportSession(session, format) {
  const datePart = session.createdAt.slice(0, 10).replace(/-/g, '');
  // A combined file says so in its name — it is the one the surveyor must not
  // confuse with a single day's survey when they come to upload.
  const base = session.sourceSessions ? `flaneur_all_${datePart}` : `flaneur_${datePart}`;

  if (format === 'osm') {
    downloadText(toOsmXml(session), `${base}.osm`, 'application/xml');
  } else if (format === 'gpx') {
    downloadText(toGpx(session), `${base}.gpx`, 'application/gpx+xml');
  } else if (format === 'geojson') {
    downloadText(toGeoJson(session), `${base}.geojson`, 'application/geo+json');
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function escXml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
